import { useEffect, useState } from "react";
import {
  Layout,
  Button,
  Card,
  Space,
  Popconfirm,
  Input,
  Spin,
  Alert,
  Descriptions,
  Typography,
} from "antd";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";
import { displayMessage, postAPI } from "../../utils/common";
import { PURGE_VENDOR, VENDOR_BY_ID } from "../../constants/api";
import { ERROR_MSG_TYPE, SUCCESS_MSG_TYPE } from "../../constants/messages";
import { HOTEL_ID_SERIES, isSuperAdmin } from "../../constants/constent";

const { Content } = Layout;
const { Text } = Typography;

const PURGE_TARGETS = [
  "Vendor profile",
  "Linked user login",
  "Documents",
  "Hotel rooms",
  "Room rate types",
  "Room facilities",
  "Bills",
  "Bill transactions",
  "Bill discount logs",
  "Bill logs",
];

function VendorPurge() {
  const navigate = useNavigate();
  const { edit_id } = useParams();

  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [confirmText, setConfirmText] = useState("");

  /* ================= FETCH ================= */

  const fetchVendor = () => {
    if (!edit_id) return;
    setLoading(true);
    postAPI(
      VENDOR_BY_ID,
      { id: edit_id },
      ({ status, data }) => {
        if (status && data) {
          setVendor(data);
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };

  useEffect(() => {
    fetchVendor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= PURGE ================= */

  const handlePurge = () => {
    setPurging(true);
    postAPI(
      PURGE_VENDOR,
      { id: vendor.id },
      (res) => {
        setPurging(false);
        if (res.status) {
          displayMessage(SUCCESS_MSG_TYPE, res.message);
          navigate("/vendor");
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
      },
      () => {
        setPurging(false);
        displayMessage(ERROR_MSG_TYPE, "Failed to purge vendor data");
      },
    );
  };

  /* ================= CLIENT GATE ================= */
  // Client gate only; the server gate is authoritative.
  if (!isSuperAdmin()) {
    return <Navigate to="/vendor" replace />;
  }

  const confirmMatches =
    !!vendor && confirmText.trim() === (vendor.hotel_name || "").trim();

  /* ================= RETURN ================= */

  return (
    <Layout>
      <LayoutHeader />
      <Content className="p-4" style={{ padding: 20 }}>
        <Spin spinning={loading}>
          <Card
            title="Delete All Vendor Data"
            extra={
              <Button onClick={() => navigate(-1)}>Go Back</Button>
            }
          >
            <Alert
              type="error"
              showIcon
              message="This action is irreversible"
              description="Permanently deleting a vendor removes the vendor and every related record from the database. This cannot be undone."
              style={{ marginBottom: 20 }}
            />

            {vendor && (
              <>
                <Descriptions
                  bordered
                  size="small"
                  column={{ xs: 1, sm: 1, md: 2 }}
                  style={{ marginBottom: 20 }}
                >
                  <Descriptions.Item label="Hotel ID">
                    {HOTEL_ID_SERIES + vendor.hotel_id}
                  </Descriptions.Item>
                  <Descriptions.Item label="Hotel Name">
                    {vendor.hotel_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Vendor Name">
                    {vendor.vendor_name}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {vendor.hotel_email_id}
                  </Descriptions.Item>
                </Descriptions>

                <div style={{ marginBottom: 20 }}>
                  <Text strong>The following will be permanently deleted:</Text>
                  <ul>
                    {PURGE_TARGETS.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Text>
                    Type the exact hotel name{" "}
                    <Text code>{vendor.hotel_name}</Text> to enable deletion:
                  </Text>
                  <Input
                    placeholder="Enter hotel name to confirm"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    style={{ marginTop: 8, maxWidth: 400 }}
                  />
                </div>

                <Space>
                  <Popconfirm
                    title="Delete all vendor data?"
                    description="This permanently removes the vendor and all related records."
                    okText="Yes, Delete"
                    cancelText="No"
                    okButtonProps={{ danger: true }}
                    disabled={!confirmMatches}
                    onConfirm={handlePurge}
                  >
                    <Button
                      danger
                      type="primary"
                      loading={purging}
                      disabled={!confirmMatches || purging}
                    >
                      Delete All Data
                    </Button>
                  </Popconfirm>
                </Space>
              </>
            )}
          </Card>
        </Spin>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default VendorPurge;
